import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Loader2, ArrowLeft, Instagram, ExternalLink, MapPin, IndianRupee, CheckCircle2, XCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
};
const fmtInr = (n) => (n == null || n === '' ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

// Brand-facing review surface. Per-creator Shortlist / Reject / Undecided actions.
// Counters update optimistically AND reconcile from backend response.
export const BrandReviewListDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rl, setRl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | undecided | shortlisted | rejected
  const [busyCreator, setBusyCreator] = useState(null);
  const [finalizing, setFinalizing] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/review-lists/${id}`, { withCredentials: true });
      setRl(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
      navigate('/brand/review-lists');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const locked = rl && rl.status !== 'pending_brand_review';
  const summary = rl?.summary || { total: 0, shortlisted: 0, rejected: 0, remaining: 0 };

  // Optimistic decision update. On error we rollback by reloading.
  const decide = async (creatorId, decision) => {
    if (locked) { toast.error('This list is locked'); return; }
    const prev = rl;
    setBusyCreator(creatorId);
    setRl((curr) => {
      if (!curr) return curr;
      const creators = curr.creators.map((c) => c.id === creatorId ? { ...c, decision } : c);
      const sl = creators.filter((c) => c.decision === 'shortlisted').length;
      const rj = creators.filter((c) => c.decision === 'rejected').length;
      const total = creators.length;
      return { ...curr, creators, summary: { ...curr.summary, total, shortlisted: sl, rejected: rj, remaining: total - sl - rj } };
    });
    try {
      const { data } = await axios.post(
        `${API_URL}/api/review-lists/${id}/creators/${creatorId}/decision`,
        { decision },
        { withCredentials: true },
      );
      // Reconcile counters with the source of truth
      setRl((curr) => curr ? { ...curr, summary: { ...curr.summary, ...data.summary } } : curr);
    } catch (err) {
      setRl(prev);
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusyCreator(null);
    }
  };

  const finalize = async () => {
    if (!window.confirm('Lock your selections? You won\'t be able to change them after this.')) return;
    setFinalizing(true);
    try {
      await axios.post(`${API_URL}/api/review-lists/${id}/finalize`, {}, { withCredentials: true });
      toast.success('Selections finalized');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Finalize failed');
    } finally { setFinalizing(false); }
  };

  const filtered = useMemo(() => {
    if (!rl) return [];
    if (filter === 'all') return rl.creators || [];
    return (rl.creators || []).filter((c) => (c.decision || 'undecided') === filter);
  }, [rl, filter]);

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center bg-white dark:bg-[#0A0B0D]">
          <Loader2 className="animate-spin text-[#FF451A]" size={32} />
        </div>
      </div>
    );
  }
  if (!rl) return null;

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="brand-review-detail-page">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => navigate('/brand/review-lists')} className="text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-white flex items-center gap-1 mb-6" data-testid="brand-review-back-button">
            <ArrowLeft size={14} /> All review lists
          </button>

          <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-1" data-testid="brand-review-title">{rl.name}</h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">{rl.campaign_name}{rl.notes && <> · <span className="text-[#6B6B6B] dark:text-zinc-500 italic">{rl.notes}</span></>}</p>
            </div>
            {!locked ? (
              <Button onClick={finalize} disabled={finalizing} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-[#E63E17] shadow-[0_4px_20px_rgba(255,69,26,0.3)] flex items-center gap-1.5 disabled:opacity-40" data-testid="brand-finalize-button">
                {finalizing ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />} Finalize Selection
              </Button>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono px-3 py-1.5 rounded-full bg-[#00C972]/10 text-[#00C972] border border-[#00C972]/30" data-testid="brand-finalized-pill">
                <Lock size={11} /> {rl.status === 'outreach_started' ? 'Outreach started' : 'Finalized'}
              </span>
            )}
          </div>

          {/* Counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="brand-counters">
            <Tile label="Total"       value={summary.total}       color="#71717a" testid="counter-total" />
            <Tile label="Shortlisted" value={summary.shortlisted} color="#00C972" testid="counter-shortlisted" />
            <Tile label="Rejected"    value={summary.rejected}    color="#FF3B30" testid="counter-rejected" />
            <Tile label="Remaining"   value={summary.remaining}   color="#FFB800" testid="counter-remaining" />
          </div>

          {/* Filter chips */}
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="brand-filter-chips">
            {[
              { k: 'all',         label: 'All' },
              { k: 'undecided',   label: 'Undecided' },
              { k: 'shortlisted', label: 'Shortlisted' },
              { k: 'rejected',    label: 'Rejected' },
            ].map((f) => (
              <button key={f.k} onClick={() => setFilter(f.k)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  filter === f.k
                    ? 'bg-[#FF451A] text-[#0F0F0F] dark:text-white border border-[#FF451A]'
                    : 'bg-white/5 text-zinc-300 border border-black/[0.10] dark:border-white/10 hover:bg-white/10'
                }`}
                data-testid={`brand-filter-${f.k}`}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Creator cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="brand-review-creators">
            {filtered.length === 0 ? (
              <div className="col-span-full bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center text-sm text-[#6B6B6B] dark:text-zinc-500">
                No creators match this filter.
              </div>
            ) : filtered.map((c) => (
              <CreatorCard
                key={c.id} c={c}
                locked={locked}
                busy={busyCreator === c.id}
                onDecide={(d) => decide(c.id, d)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Tile = ({ label, value, color, testid }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">{label}</div>
    <div className="text-2xl font-bold font-mono" style={{ color }}>{value}</div>
  </div>
);

const CreatorCard = ({ c, locked, busy, onDecide }) => {
  const igUrl = c.instagram_handle ? `https://instagram.com/${c.instagram_handle.replace(/^@/, '')}` : null;
  const dec = c.decision || 'undecided';
  const borderColor =
    dec === 'shortlisted' ? '#00C972' :
    dec === 'rejected' ? '#FF3B30' : 'rgba(255,255,255,0.05)';
  return (
    <div className="bg-white dark:bg-[#0A0B0D] rounded-2xl p-5 border transition-colors" style={{ borderColor }} data-testid="brand-creator-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white text-base font-bold shrink-0">
          {c.name?.[0]?.toUpperCase() || 'C'}
        </div>
        <div className="flex-1 min-w-0">
          {/* Allow the brand to open the full creator profile in a new tab */}
          <Link to={`/brand/creators/${c.id}`} target="_blank" className="text-base font-semibold text-[#0F0F0F] dark:text-white hover:text-[#FF451A] inline-flex items-center gap-1 transition-colors" data-testid="brand-creator-profile-link">
            {c.name} <ExternalLink size={11} />
          </Link>
          {c.instagram_handle && (
            <a href={igUrl} target="_blank" rel="noreferrer" className="text-xs text-[#6B6B6B] dark:text-zinc-400 hover:text-[#E1306C] inline-flex items-center gap-1 transition-colors block">
              <Instagram size={11} /> @{c.instagram_handle}
            </a>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full shrink-0"
          style={{
            backgroundColor: dec === 'shortlisted' ? '#00C97215' : dec === 'rejected' ? '#FF3B3015' : '#71717a15',
            color: dec === 'shortlisted' ? '#00C972' : dec === 'rejected' ? '#FF3B30' : '#71717a',
            border: `1px solid ${dec === 'shortlisted' ? '#00C97230' : dec === 'rejected' ? '#FF3B3030' : '#71717a30'}`,
          }}
          data-testid="brand-creator-decision-pill">
          {dec}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs mb-3" data-testid="brand-creator-stats">
        <Stat label="Followers" value={fmtNum(c.instagram_followers)} />
        <Stat label="Reel" value={fmtInr(c.pricing_reel)} />
        <Stat label="Story" value={fmtInr(c.pricing_story)} />
      </div>

      <div className="flex flex-wrap gap-1.5 text-[11px] mb-3">
        {(c.categories || []).slice(0, 3).map((cat) => (
          <span key={cat} className="px-2 py-0.5 rounded-full bg-[#FF451A]/10 text-[#FF451A] border border-[#FF451A]/20 font-medium uppercase tracking-wider">{cat}</span>
        ))}
        {(c.cities || []).slice(0, 2).map((city) => (
          <span key={city} className="px-2 py-0.5 rounded-full bg-white/5 text-zinc-300 border border-black/[0.10] dark:border-white/10 font-medium inline-flex items-center gap-1">
            <MapPin size={9} /> {city}
          </span>
        ))}
        {c.quality_grade && <span className="px-2 py-0.5 rounded-full bg-white/5 text-zinc-300 border border-black/[0.10] dark:border-white/10 font-mono font-medium">{c.quality_grade}</span>}
        {c.brand_safety_score != null && <span className="px-2 py-0.5 rounded-full bg-white/5 text-zinc-300 border border-black/[0.10] dark:border-white/10 font-mono font-medium">Safety {c.brand_safety_score}</span>}
      </div>

      {!locked && (
        <div className="flex gap-2" data-testid="brand-decision-buttons">
          <Button onClick={() => onDecide('rejected')} disabled={busy}
            className={`flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors inline-flex items-center justify-center gap-1 ${
              dec === 'rejected' ? 'bg-[#FF3B30] text-[#0F0F0F] dark:text-white' : 'bg-white/5 text-[#0F0F0F] dark:text-white border border-black/[0.10] dark:border-white/10 hover:bg-white/10'
            } disabled:opacity-40`}
            data-testid="brand-reject-button">
            <XCircle size={12} /> Reject
          </Button>
          <Button onClick={() => onDecide('undecided')} disabled={busy}
            className={`flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              dec === 'undecided' ? 'bg-[#FFB800] text-black' : 'bg-white/5 text-[#0F0F0F] dark:text-white border border-black/[0.10] dark:border-white/10 hover:bg-white/10'
            } disabled:opacity-40`}
            data-testid="brand-undecide-button">
            Undecided
          </Button>
          <Button onClick={() => onDecide('shortlisted')} disabled={busy}
            className={`flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors inline-flex items-center justify-center gap-1 ${
              dec === 'shortlisted' ? 'bg-[#00C972] text-[#0F0F0F] dark:text-white' : 'bg-white/5 text-[#0F0F0F] dark:text-white border border-black/[0.10] dark:border-white/10 hover:bg-white/10'
            } disabled:opacity-40`}
            data-testid="brand-shortlist-button">
            <CheckCircle2 size={12} /> Shortlist
          </Button>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-white/5 border border-black/[0.10] dark:border-white/10 rounded-lg p-2">
    <div className="text-[9px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">{label}</div>
    <div className="text-sm font-bold text-[#0F0F0F] dark:text-white font-mono">{value}</div>
  </div>
);
